import { useCallback, useEffect, useRef, useState } from 'react';
import { useLoggerContext } from '../context/LoggerContext';
import { Generic } from '../interfaces/interfaces';
import { genericRequest } from '../store/Actions';

const htmlContent = window.topContext.config.getEndpointConfig().htmlContent;
const basePath = htmlContent + '/?documentPath=shared/content-blocks/privat/selvbetjening/kobsflow/common-online-flows';

type UseFetchContent = {
    urls: { prop: string; url: string }[];
    timeout?: number;
};

const cache: Generic<string> = {};

export const useFetchContent = ({ urls, timeout = 5000 }: UseFetchContent) => {
    const { logFetchContentError } = useLoggerContext();
    const isLoading = useRef(false);

    const [state, setState] = useState({
        contentBlocks: {} as { [key: string]: string },
        isLoading: true,
        error: undefined as unknown
    });

    const fetchAll = useCallback(() => {
        const fetchCalls: Record<string, Promise<string>>[] = [];
        for (const url of urls) {
            // if content is cached - get it from the cache
            if (cache[url.prop]) {
                setState((prev) => ({
                    ...prev,
                    contentBlocks: { ...prev.contentBlocks, [url.prop]: cache[url.prop] }
                }));
            } else {
                fetchCalls.push({
                    [url.prop]: genericRequest({
                        method: 'GET',
                        url: basePath + url.url,
                        headers: {},
                        timeout: timeout
                    })
                });
            }
        }

        return fetchCalls;
    }, [urls, timeout]);

    useEffect(() => {
        const fetchContent = async () => {
            try {
                const responses = await Promise.all(fetchAll());
                responses.forEach(async (response, index) => {
                    const text = await response[urls[index].prop];

                    setState((prev) => ({
                        ...prev,
                        contentBlocks: { ...prev.contentBlocks, [urls[index].prop]: text }
                    }));

                    cache[urls[index].prop] = text;
                });
            } catch (error) {
                setState({
                    contentBlocks: {},
                    isLoading: false,
                    error: true
                });
                logFetchContentError(JSON.stringify(error));
            }
        };

        if (!isLoading.current) {
            isLoading.current = true;
            setState((prev) => ({ ...prev, isLoading: true }));
            fetchContent();
        }
    }, [urls, fetchAll, logFetchContentError]);

    return { ...state, error: !!state.error };
};
